import { z } from 'zod';
import { tryFormatJson } from '../../../../streaming-messages/try-format-json';

const WebSocketToolInputModeSchema = z.enum([
  'connect',
  'connect-and-authenticate',
  'send',
  'poll',
  'stop',
]);

const WebSocketCanonicalStatusSchema = z.enum([
  'connected',
  'sent',
  'queued',
  'polled',
  'stop_requested',
  'stopped',
  'already_stopped',
]);

const WebSocketCopilotModeSchema = z.enum([
  'connected',
  'sent',
  'queued',
  'poll',
  'stopped',
  'unknown',
]);

const WebSocketMessageSchema = z.object({
  id: z.string().optional(),
  data: z.string().optional(),
  timestamp: z.string().optional(),
  direction: z.enum(['inbound', 'outbound']).optional(),
  eventType: z.string().optional(),
  type: z.string().optional(),
});

const WebSocketToolInputSchema = z.object({
  mode: WebSocketToolInputModeSchema.optional(),
  url: z.string().optional(),
  sessionId: z.string().optional(),
  message: z.string().optional(),
  initialMessage: z.string().optional(),
});

const WebSocketToolOutputSchema = z.object({
  status: z.string().optional(),
  mode: WebSocketCopilotModeSchema.optional(),
  sessionId: z.string().optional(),
  url: z.string().optional(),
  message: z.string().optional(),
  count: z.number().optional(),
  messages: z.array(WebSocketMessageSchema).optional(),
  isActive: z.boolean().optional(),
  rawResult: z.string().optional(),
});

export type WebSocketExecutionKind = 'connect' | 'send' | 'poll' | 'stop';

export type WebSocketStopOutcome =
  | 'stop_requested'
  | 'stopped'
  | 'already_stopped'
  | 'failed';

export type WebSocketInboundMessage = {
  id: string;
  data: string;
  timestamp?: string;
};

export type WebSocketConnectView = {
  kind: 'connect';
  url?: string;
  sessionId?: string;
  authenticated?: boolean;
  initialMessage?: string;
  error?: string;
};

export type WebSocketSendView = {
  kind: 'send';
  sessionId?: string;
  queued: boolean;
  payload?: string;
  formattedPayload?: string;
  error?: string;
};

export type WebSocketPollView = {
  kind: 'poll';
  sessionId?: string;
  isActive?: boolean;
  messages: WebSocketInboundMessage[];
  count: number;
  error?: string;
};

export type WebSocketStopView = {
  kind: 'stop';
  sessionId?: string;
  outcome: WebSocketStopOutcome;
  error?: string;
};

export type WebSocketUnknownView = {
  kind: 'unknown';
  raw?: string;
  error?: string;
};

export type WebSocketExecutionView =
  | WebSocketConnectView
  | WebSocketSendView
  | WebSocketPollView
  | WebSocketStopView
  | WebSocketUnknownView;

type ParsedWebSocketInput = z.infer<typeof WebSocketToolInputSchema>;
type ParsedWebSocketOutput = z.infer<typeof WebSocketToolOutputSchema>;
type WebSocketCanonicalStatus = z.infer<typeof WebSocketCanonicalStatusSchema>;
type WebSocketCopilotMode = z.infer<typeof WebSocketCopilotModeSchema>;
type WebSocketToolInputMode = z.infer<typeof WebSocketToolInputModeSchema>;

type JsonParseOutcome =
  | { kind: 'value'; value: unknown }
  | { kind: 'raw'; raw: string }
  | { kind: 'empty' };

const parseJsonValue = (value: unknown): JsonParseOutcome => {
  if (value === undefined || value === null) {
    return { kind: 'empty' };
  }

  if (typeof value !== 'string') {
    return { kind: 'value', value };
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return { kind: 'empty' };
  }

  try {
    return { kind: 'value', value: JSON.parse(trimmed) };
  } catch {
    return { kind: 'raw', raw: value };
  }
};

const kindFromCanonicalStatus = (
  status: WebSocketCanonicalStatus,
): WebSocketExecutionKind => {
  switch (status) {
    case 'connected':
      return 'connect';
    case 'sent':
    case 'queued':
      return 'send';
    case 'polled':
      return 'poll';
    case 'stop_requested':
    case 'stopped':
    case 'already_stopped':
      return 'stop';
    default: {
      const unhandled: never = status;

      return unhandled;
    }
  }
};

const kindFromCopilotMode = (
  mode: WebSocketCopilotMode,
): WebSocketExecutionKind | undefined => {
  switch (mode) {
    case 'connected':
      return 'connect';
    case 'sent':
    case 'queued':
      return 'send';
    case 'poll':
      return 'poll';
    case 'stopped':
      return 'stop';
    case 'unknown':
      return undefined;
    default: {
      const unhandled: never = mode;

      return unhandled;
    }
  }
};

const kindFromInputMode = (
  mode: WebSocketToolInputMode,
): WebSocketExecutionKind => {
  switch (mode) {
    case 'connect':
    case 'connect-and-authenticate':
      return 'connect';
    case 'send':
      return 'send';
    case 'poll':
      return 'poll';
    case 'stop':
      return 'stop';
    default: {
      const unhandled: never = mode;

      return unhandled;
    }
  }
};

const resolveKind = ({
  output,
  inputMode,
}: {
  output: ParsedWebSocketOutput | undefined;
  inputMode: WebSocketToolInputMode | undefined;
}): WebSocketExecutionKind | undefined => {
  if (output) {
    const canonical = WebSocketCanonicalStatusSchema.safeParse(output.status);

    if (canonical.success) {
      return kindFromCanonicalStatus(canonical.data);
    }

    if (output.mode) {
      const copilotKind = kindFromCopilotMode(output.mode);

      if (copilotKind) {
        return copilotKind;
      }
    }

    if (output.status === 'active' && output.url) {
      return 'connect';
    }

    if (output.messages !== undefined) {
      return 'poll';
    }
  }

  if (inputMode) {
    return kindFromInputMode(inputMode);
  }

  return undefined;
};

const resolveStopOutcome = ({
  output,
  error,
}: {
  output: ParsedWebSocketOutput | undefined;
  error?: string;
}): WebSocketStopOutcome => {
  if (error) {
    return 'failed';
  }

  if (output?.status === 'stop_requested') {
    return 'stop_requested';
  }

  if (output?.status === 'already_stopped') {
    return 'already_stopped';
  }

  return 'stopped';
};

const toInboundMessages = (
  messages: ParsedWebSocketOutput['messages'],
): WebSocketInboundMessage[] =>
  (messages ?? [])
    .filter((message) => message.direction === 'inbound')
    .map((message, index) => ({
      id: message.id ?? `inbound-${index}`,
      data: message.data ?? '',
      timestamp: message.timestamp,
    }));

const stringifyUnknown = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }

  if (value === undefined || value === null) {
    return undefined;
  }

  try {
    return JSON.stringify(value, undefined, 2);
  } catch {
    return undefined;
  }
};

const buildView = ({
  kind,
  input,
  output,
  error,
  rawOutput,
}: {
  kind: WebSocketExecutionKind;
  input: ParsedWebSocketInput;
  output: ParsedWebSocketOutput | undefined;
  error?: string;
  rawOutput?: string;
}): WebSocketExecutionView => {
  const sessionId = output?.sessionId ?? input.sessionId;

  switch (kind) {
    case 'connect':
      return {
        kind,
        url: output?.url ?? input.url,
        sessionId,
        authenticated: input.mode === 'connect-and-authenticate' && !error,
        initialMessage: input.initialMessage,
        error,
      };
    case 'send': {
      const payload = input.message;

      return {
        kind,
        sessionId,
        queued: output?.status === 'queued' || output?.mode === 'queued',
        payload,
        formattedPayload: payload
          ? (tryFormatJson(payload) ?? payload)
          : undefined,
        error,
      };
    }

    case 'poll': {
      const messages = toInboundMessages(output?.messages);

      return {
        kind,
        sessionId,
        isActive: output?.isActive,
        messages,
        count: messages.length,
        error,
      };
    }

    case 'stop':
      return {
        kind,
        sessionId,
        outcome: resolveStopOutcome({ output, error }),
        error,
      };
    default: {
      const unhandled: never = kind;

      return {
        kind: 'unknown',
        raw: rawOutput,
        error: error ?? String(unhandled),
      };
    }
  }
};

export const parseWebSocketExecutionView = ({
  input,
  output,
  error,
}: {
  input?: unknown;
  output?: unknown;
  error?: string;
}): WebSocketExecutionView => {
  const explicitError = error?.trim() ? error : undefined;
  const parsedInput = WebSocketToolInputSchema.safeParse(input ?? {});
  const safeInput: ParsedWebSocketInput = parsedInput.success
    ? parsedInput.data
    : {};

  const outputForParse =
    explicitError && output === explicitError ? undefined : output;
  const jsonOutcome = parseJsonValue(outputForParse);

  if (jsonOutcome.kind === 'raw') {
    const kind = resolveKind({
      output: undefined,
      inputMode: safeInput.mode,
    });

    if (!kind) {
      return {
        kind: 'unknown',
        raw: jsonOutcome.raw,
        error: explicitError,
      };
    }

    return buildView({
      kind,
      input: safeInput,
      output: undefined,
      error: explicitError,
      rawOutput: jsonOutcome.raw,
    });
  }

  const parsedOutput =
    jsonOutcome.kind === 'value'
      ? WebSocketToolOutputSchema.safeParse(jsonOutcome.value)
      : undefined;
  const safeOutput = parsedOutput?.success ? parsedOutput.data : undefined;
  const kind = resolveKind({
    output: safeOutput,
    inputMode: safeInput.mode,
  });

  if (!kind) {
    return {
      kind: 'unknown',
      raw: safeOutput?.rawResult ?? stringifyUnknown(outputForParse),
      error: explicitError,
    };
  }

  return buildView({
    kind,
    input: safeInput,
    output: safeOutput,
    error: explicitError,
    rawOutput: stringifyUnknown(outputForParse),
  });
};
